import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Crown, Edit, Trash2, Plus, Users, Award, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  { value: 'Mayor', label_en: 'Mayor', label_mr: 'महापौर' },
  { value: 'Deputy Mayor', label_en: 'Deputy Mayor', label_mr: 'उपमहापौर' },
  { value: 'Standing Committee Chairman', label_en: 'Standing Committee Chairman', label_mr: 'स्थायी समिती सभापती' }
];

const PARTIES = [
  { value: 'BJP', label: 'BJP (भारतीय जनता पक्ष)', color: '#FF9933' },
  { value: 'Congress', label: 'Congress (काँग्रेस)', color: '#19AAED' },
  { value: 'NCP', label: 'NCP (राष्ट्रवादी काँग्रेस)', color: '#00BFFF' },
  { value: 'Shiv Sena', label: 'Shiv Sena (शिवसेना)', color: '#FF6600' },
  { value: 'Independent', label: 'Independent (अपक्ष)', color: '#808080' }
];

const LeadershipSelection = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaders, setLeaders] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    position: '',
    candidate_name: '',
    candidate_name_marathi: '',
    party_name: '',
    total_votes: 0,
    win_by_votes: 0,
    tatasth_votes_needed: 0,
    elected_date: '',
    status: 'Elected',
    photo_url: ''
  });

  const t = (en, mr) => language === 'mr' ? mr : en;

  useEffect(() => {
    fetchLeaders();
  }, []);

  const fetchLeaders = async () => {
    try {
      const response = await axios.get(`${API}/leadership-selection`);
      setLeaders(response.data);
    } catch (error) {
      console.error('Error fetching leaders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedLeader(null);
    setPhotoPreview(null);
    setFormData({
      position: '',
      candidate_name: '',
      candidate_name_marathi: '',
      party_name: '',
      total_votes: 0,
      win_by_votes: 0,
      tatasth_votes_needed: 0,
      elected_date: new Date().toISOString().split('T')[0],
      status: 'Elected',
      photo_url: ''
    });
    setEditDialog(true);
  };

  const handleEdit = (leader) => {
    setSelectedLeader(leader);
    setPhotoPreview(leader.photo_url || null);
    setFormData({
      position: leader.position || '',
      candidate_name: leader.candidate_name || '',
      candidate_name_marathi: leader.candidate_name_marathi || '',
      party_name: leader.party_name || '',
      total_votes: leader.total_votes || 0,
      win_by_votes: leader.win_by_votes || 0,
      tatasth_votes_needed: leader.tatasth_votes_needed || 0,
      elected_date: leader.elected_date || '',
      status: leader.status || 'Elected',
      photo_url: leader.photo_url || ''
    });
    setEditDialog(true);
  };

  const handleDelete = (leader) => {
    setSelectedLeader(leader);
    setDeleteDialog(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t('Photo size should be less than 2MB', 'फोटो आकार 2MB पेक्षा कमी असावा'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFormData({...formData, photo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedLeader) {
        // Update existing
        await axios.put(`${API}/leadership-selection/${selectedLeader.id}`, {
          ...formData,
          id: selectedLeader.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('Leader updated successfully', 'नेता यशस्वीरित्या अपडेट केला'));
      } else {
        // Create new
        await axios.post(`${API}/leadership-selection`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('Leader added successfully', 'नेता यशस्वीरित्या जोडला'));
      }
      setEditDialog(false);
      fetchLeaders();
    } catch (error) {
      toast.error(t('Error saving leader', 'नेता सेव्ह करताना त्रुटी'));
      console.error('Error:', error);
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/leadership-selection/${selectedLeader.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('Leader deleted successfully', 'नेता यशस्वीरित्या हटवला'));
      setDeleteDialog(false);
      fetchLeaders();
    } catch (error) {
      toast.error(t('Error deleting leader', 'नेता हटवताना त्रुटी'));
      console.error('Error:', error);
    }
  };

  const getPartyColor = (partyName) => {
    const party = PARTIES.find(p => p.value === partyName);
    return party ? party.color : '#808080';
  };

  const getPositionIcon = (position) => {
    if (position === 'Mayor') return <Crown className="h-5 w-5 text-yellow-500" />;
    if (position === 'Deputy Mayor') return <Award className="h-5 w-5 text-blue-500" />;
    return <Users className="h-5 w-5 text-green-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{t('Loading...', 'लोड होत आहे...')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leadership-selection-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
            {t('Leadership Selection', 'नेतृत्व निवड')}
          </h1>
          <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
            {t('Mayor, Deputy Mayor & Standing Committee Chairman', 'महापौर, उपमहापौर आणि स्थायी समिती सभापती')}
          </p>
        </div>
        <Button onClick={handleAdd} className="bg-primary" data-testid="add-leader-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('Add Leader', 'नेता जोडा')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {POSITIONS.map((pos) => {
          const leader = leaders.find(l => l.position === pos.value);
          return (
            <Card key={pos.value} className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {getPositionIcon(pos.value)}
                  {language === 'mr' ? pos.label_mr : pos.label_en}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leader ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      {leader.photo_url ? (
                        <img src={leader.photo_url} alt={leader.candidate_name} className="w-16 h-16 rounded-full object-cover border-2 border-primary" />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-xl font-bold">{leader.candidate_name}</p>
                        {leader.candidate_name_marathi && (
                          <p className="text-sm text-muted-foreground marathi-text">{leader.candidate_name_marathi}</p>
                        )}
                        <Badge style={{ backgroundColor: getPartyColor(leader.party_name) }} className="text-white mt-1">
                          {leader.party_name}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground mt-2">
                      <p>{t('Total Votes', 'एकूण मते')}: <strong>{leader.total_votes?.toLocaleString()}</strong></p>
                      <p>{t('Against Votes', 'विरोधी मते')}: <strong>{leader.win_by_votes?.toLocaleString()}</strong></p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">
                    {t('Not yet elected', 'अद्याप निवडलेले नाही')}
                  </p>
                )}}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Leaders Table */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            {t('Leadership Details', 'नेतृत्व तपशील')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#003366]">
                <TableHead className="text-white">{t('Photo', 'फोटो')}</TableHead>
                <TableHead className="text-white">{t('Position', 'पद')}</TableHead>
                <TableHead className="text-white">{t('Candidate Name', 'उमेदवाराचे नाव')}</TableHead>
                <TableHead className="text-white">{t('Party', 'पक्ष')}</TableHead>
                <TableHead className="text-white text-right">{t('Total Votes', 'एकूण मते')}</TableHead>
                <TableHead className="text-white text-right">{t('Against Votes', 'विरोधी मते')}</TableHead>
                <TableHead className="text-white text-right">{t('Tatasth Votes', 'तटस्थ मते')}</TableHead>
                <TableHead className="text-white">{t('Status', 'स्थिती')}</TableHead>
                <TableHead className="text-white text-center">{t('Actions', 'क्रिया')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {t('No leaders added yet. Click "Add Leader" to add.', 'अद्याप कोणतेही नेते जोडलेले नाहीत. जोडण्यासाठी "नेता जोडा" वर क्लिक करा.')}
                  </TableCell>
                </TableRow>
              ) : (
                leaders.map((leader, idx) => (
                  <TableRow key={leader.id || idx} className="hover:bg-muted/50">
                    <TableCell>
                      {leader.photo_url ? (
                        <img src={leader.photo_url} alt={leader.candidate_name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {getPositionIcon(leader.position)}
                        {leader.position}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">
                      <div>
                        <p>{leader.candidate_name}</p>
                        {leader.candidate_name_marathi && (
                          <p className="text-sm text-muted-foreground marathi-text">{leader.candidate_name_marathi}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge style={{ backgroundColor: getPartyColor(leader.party_name) }} className="text-white">
                        {leader.party_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{leader.total_votes?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-green-600 font-medium">{leader.win_by_votes?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-orange-600 font-medium">{leader.tatasth_votes_needed?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant={leader.status === 'Elected' ? 'default' : leader.status === 'Not Elected' ? 'destructive' : 'secondary'}>
                        {leader.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(leader)}
                          data-testid={`edit-${leader.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(leader)}
                          data-testid={`delete-${leader.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className={language === 'mr' ? 'marathi-text' : ''}>
              {selectedLeader 
                ? t('Edit Leader', 'नेता संपादित करा') 
                : t('Add New Leader', 'नवीन नेता जोडा')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>{t('Candidate Photo', 'उमेदवाराचा फोटो')}</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload">
                    <Button type="button" variant="outline" className="cursor-pointer" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {t('Upload Photo', 'फोटो अपलोड करा')}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Max 2MB, JPG/PNG', 'कमाल 2MB, JPG/PNG')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('Position', 'पद')} *</Label>
              <Select value={formData.position} onValueChange={(v) => setFormData({...formData, position: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Select Position', 'पद निवडा')} />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((pos) => (
                    <SelectItem key={pos.value} value={pos.value}>
                      {language === 'mr' ? pos.label_mr : pos.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('Candidate Name (English)', 'उमेदवाराचे नाव (इंग्रजी)')} *</Label>
              <Input
                value={formData.candidate_name}
                onChange={(e) => setFormData({...formData, candidate_name: e.target.value})}
                placeholder={t('Enter candidate name in English', 'इंग्रजीमध्ये उमेदवाराचे नाव प्रविष्ट करा')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('Candidate Name (Marathi)', 'उमेदवाराचे नाव (मराठी)')} *</Label>
              <Input
                value={formData.candidate_name_marathi}
                onChange={(e) => setFormData({...formData, candidate_name_marathi: e.target.value})}
                placeholder={t('Enter candidate name in Marathi', 'मराठीमध्ये उमेदवाराचे नाव प्रविष्ट करा')}
                className="marathi-text"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('Party Name', 'पक्षाचे नाव')} *</Label>
              <Select value={formData.party_name} onValueChange={(v) => setFormData({...formData, party_name: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Select Party', 'पक्ष निवडा')} />
                </SelectTrigger>
                <SelectContent>
                  {PARTIES.map((party) => (
                    <SelectItem key={party.value} value={party.value}>
                      {party.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Total Votes', 'एकूण मते')}</Label>
                <Input
                  type="number"
                  value={formData.total_votes}
                  onChange={(e) => setFormData({...formData, total_votes: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('Against Votes', 'विरोधी मते')}</Label>
                <Input
                  type="number"
                  value={formData.win_by_votes}
                  onChange={(e) => setFormData({...formData, win_by_votes: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('Tatasth (Neutral) Votes', 'तटस्थ मते')}</Label>
              <Input
                type="number"
                value={formData.tatasth_votes_needed}
                onChange={(e) => setFormData({...formData, tatasth_votes_needed: parseInt(e.target.value) || 0})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Elected Date', 'निवड दिनांक')}</Label>
                <Input
                  type="date"
                  value={formData.elected_date}
                  onChange={(e) => setFormData({...formData, elected_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('Status', 'स्थिती')}</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Elected">{t('Elected', 'निवडलेले')}</SelectItem>
                    <SelectItem value="Not Elected">{t('Not Elected', 'निवडलेले नाही')}</SelectItem>
                    <SelectItem value="Pending">{t('Pending', 'प्रलंबित')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              {t('Cancel', 'रद्द करा')}
            </Button>
            <Button onClick={handleSave} disabled={!formData.position || !formData.candidate_name || !formData.party_name}>
              {t('Save', 'सेव्ह करा')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={language === 'mr' ? 'marathi-text' : ''}>
              {t('Confirm Delete', 'हटवणे निश्चित करा')}
            </DialogTitle>
          </DialogHeader>
          <p className={language === 'mr' ? 'marathi-text' : ''}>
            {t('Are you sure you want to delete', 'तुम्हाला खात्री आहे की तुम्ही हटवू इच्छिता')} <strong>{selectedLeader?.candidate_name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              {t('Cancel', 'रद्द करा')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              {t('Delete', 'हटवा')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadershipSelection;
